/**
 * @fileoverview Axios client with Supabase JWT and multi-tenant headers.
 * @module lib/apiClient
 */

import axios from 'axios'
import {
  getAccessToken,
  getCachedAccessTokenFromStorage,
  getAccessTokenSyncWithSource,
  hasCachedSupabaseToken,
} from './supabaseClient'
import { devlog } from '@/shared/utils/devlog'

interface JwtPayload {
  exp?: number
}

const CLOCK_SKEW_SECONDS = 5
const TOKEN_RESOLVE_TIMEOUT_MS = 3000

function base64UrlDecode(input: string): string {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/')
  const padLength = 4 - (normalized.length % 4 || 4)
  const padded = normalized + '='.repeat(padLength === 4 ? 0 : padLength)
  try {
    if (typeof window !== 'undefined' && typeof window.atob === 'function') {
      return window.atob(padded)
    }
    const maybeBuffer = (
      globalThis as {
        Buffer?: {
          from: (input: string, encoding: string) => { toString: (enc: string) => string }
        }
      }
    ).Buffer
    if (maybeBuffer) {
      return maybeBuffer.from(padded, 'base64').toString('utf-8')
    }
    return ''
  } catch {
    return ''
  }
}

function getJwtExpiry(token: string): number | null {
  try {
    const [, payload] = token.split('.')
    if (!payload) return null
    const decoded = base64UrlDecode(payload)
    if (!decoded) return null
    const parsed = JSON.parse(decoded) as JwtPayload
    return typeof parsed.exp === 'number' ? parsed.exp : null
  } catch {
    return null
  }
}

function isJwtExpired(token: string, nowSeconds = Math.floor(Date.now() / 1000)): boolean {
  const exp = getJwtExpiry(token)
  if (!exp) return false
  return exp <= nowSeconds + CLOCK_SKEW_SECONDS
}

/**
 * Resolve API base URL:
 * - Use VITE_API_URL when provided (e.g., in production or custom dev setups)
 * - Otherwise use a relative '/api' to route through Vite dev proxy
 */
const API_BASE_URL: string = (import.meta.env?.VITE_API_URL as string) || '/api'

/**
 * Race Supabase token retrieval against a timeout and fallback to cached storage value.
 * Interceptors should never stall; this guarantees a bounded wait.
 *
 * @param timeoutMs - Max duration to wait for Supabase getSession, default 3000ms
 * @param options - Behaviour flags controlling cache usage
 * @returns Promise resolving to a JWT string or null
 */
export async function getAccessTokenFast(
  timeoutMs = TOKEN_RESOLVE_TIMEOUT_MS,
  options: { skipCache?: boolean } = {}
): Promise<string | null> {
  const { skipCache = false } = options

  if (!skipCache) {
    // Fast path: use cached token immediately if present to avoid any startup stall
    const cachedFirst = getCachedAccessTokenFromStorage()
    if (cachedFirst) return cachedFirst
  }

  const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), timeoutMs))
  try {
    const token = await Promise.race([getAccessToken(), timeout])
    if (token) return token
  } catch {
    // Ignore and fall back to cached token
  }
  return skipCache ? null : getCachedAccessTokenFromStorage()
}

/**
 * Resolve a usable JWT for outbound API requests, refreshing when cached token expired.
 *
 * @param timeoutMs - Max duration allowed for Supabase session refresh attempts
 * @returns Promise resolving to a fresh or cached JWT, or null when unavailable
 * @example
 * ```ts
 * const token = await resolveAuthToken()
 * if (token) {
 *   config.headers.Authorization = `Bearer ${token}`
 * }
 * ```
 */
export async function resolveAuthToken(
  timeoutMs = TOKEN_RESOLVE_TIMEOUT_MS
): Promise<string | null> {
  const { token } = getAccessTokenSyncWithSource()
  if (token && !isJwtExpired(token)) {
    return token
  }
  const skipCache = Boolean(token)
  return getAccessTokenFast(timeoutMs, { skipCache })
}

/**
 * Decide whether a 401 should trigger a redirect to landing page.
 * Auth routes must never redirect to themselves to avoid loops.
 *
 * @param pathname - Current window location pathname
 * @returns True when redirect should be skipped
 */
export function shouldSkipAuthRedirect(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname === '/en/' ||
    pathname === '/pl/' ||
    pathname.startsWith('/en/') ||
    pathname.startsWith('/pl/') ||
    pathname === '/logout' ||
    pathname === '/signup' ||
    pathname === '/accept-invite' ||
    pathname.startsWith('/accept-invite/') ||
    pathname === '/onboarding/create-org'
  )
}

/**
 * Read active organization id from localStorage.
 * @returns org id string or null
 */
export function readActiveOrgIdFromStorage(): string | null {
  try {
    return localStorage.getItem('activeOrgId')
  } catch {
    return null
  }
}

/**
 * Creates and configures the main API client with JWT and X-Org-Id headers.
 *
 * @example
 * ```ts
 * import apiClient from '@/lib/apiClient'
 * const res = await apiClient.get('/statistics')
 * ```
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { Accept: 'application/json' },
})

// Request: add Authorization and X-Org-Id (never block)
apiClient.interceptors.request.use(
  async config => {
    // Non-blocking token retrieval with expiry guard
    const resolvedToken = await resolveAuthToken()
    if (resolvedToken) {
      ;(config.headers as Record<string, unknown>).Authorization = `Bearer ${resolvedToken}`
    }
    const orgId = readActiveOrgIdFromStorage()
    if (orgId) {
      ;(config.headers as Record<string, unknown>)['X-Org-Id'] = orgId
    }
    // Important: allow browser to set multipart boundary for FormData
    if (config.data instanceof FormData) {
      try {
        delete (config.headers as Record<string, unknown>)['Content-Type']
      } catch {
        // ignore
      }
    }
    return config
  },
  error => Promise.reject(error)
)

// Response: 401 guard with auth-route skip
apiClient.interceptors.response.use(
  response => response,
  error => {
    try {
      const status: number | undefined = error?.response?.status
      if (status === 401 && typeof window !== 'undefined') {
        const path = window.location?.pathname || '/'
        const authHeader = (error?.config?.headers as Record<string, unknown> | undefined)?.Authorization
        const hadAuthorization =
          typeof authHeader === 'string' ? authHeader.trim().length > 0 : Boolean(authHeader)
        const hasCachedToken = hasCachedSupabaseToken()
        devlog('api:401', 'received', { path, hadAuthorization, hasCachedToken })

        // Avoid full-page redirects on transient resume races where auth header was
        // not attached yet but a cached Supabase session token still exists.
        if (!hadAuthorization && hasCachedToken) {
          devlog('api:401', 'skip redirect due to likely token refresh race')
          return Promise.reject(error)
        }

        if (!shouldSkipAuthRedirect(path)) {
          devlog('api:401', 'redirecting to /')
          window.location.replace('/')
        }
      }
    } catch {
      // Ignore guard errors
    }
    return Promise.reject(error)
  }
)

export default apiClient
